import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { usePublicMenu, useSubmitMenuInquiry } from "@/hooks/usePublishedMenus";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PublicFooter } from "@/components/website/PublicFooter";
import { PublicLayout } from "@/components/website/PublicLayout";
import { HeroImage } from "@/components/website/HeroImage";
import { MenuPackagesSection } from "@/components/website/MenuPackagesSection";
import { PlanThanksBanner, PlanCallLine } from "@/components/website/PlanThanksBanner";
import { packageDisplayName, packageOptionLabel, type MenuPackage } from "@/lib/menu-packages";
import { useDietaryTags, getDietaryTagLabel } from "@/hooks/useDietaryTags";
import { usePricingTypes } from "@/hooks/usePricingTypes";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { publicMenuUrl } from "@/lib/public-urls";
import { menuPriceUnit } from "@/lib/menu-pricing";
import { ogImageUrl, plainText, menuJsonLd } from "@/lib/seo";
import { AlertTriangle } from "lucide-react";
import { z } from "zod";
import { NotFoundError } from "@/lib/queryErrors";

const inquirySchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100),
  email: z.string().trim().email("Invalid email").max(255),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  event_date: z.string().optional().or(z.literal("")),
  guest_count: z.coerce.number().int().min(1).max(10000).optional().or(z.literal("")),
  message: z.string().trim().max(2000).optional().or(z.literal("")),
});

interface MenuItemRef {
  name: string;
  description: string | null;
  dietary_tags: string[] | null;
  prices?: { price: number; pricing_type?: string | null; serves?: number | null }[];
}

interface MenuSectionItem {
  id: string;
  price_override: number | null;
  description_override: string | null;
  menu_item: MenuItemRef | null;
}

interface MenuSection {
  id: string;
  title: string;
  description: string | null;
  items: MenuSectionItem[] | null;
}

const EMPTY_FORM = { name: "", email: "", phone: "", event_date: "", guest_count: "", message: "" };
// Select value for "no package". Radix Select can't use "" as an item value.
const CUSTOM_QUOTE = "custom";
const NO_PACKAGES: MenuPackage[] = [];

export default function PublicMenuPage() {
  const { slug } = useParams<{ slug: string }>();
  // Arrived from the plan wizard (#1221): they already gave us their details,
  // so thank them and don't ask again.
  const [searchParams] = useSearchParams();
  const fromPlan = searchParams.get("from") === "plan";
  const planRef = searchParams.get("ref");
  const { data: menu, isLoading, error, refetch, isFetching } = usePublicMenu(slug || "");
  const submitInquiry = useSubmitMenuInquiry();
  const { data: dietaryTags } = useDietaryTags();
  const { data: pricingTypes } = usePricingTypes();
  const formRef = useRef<HTMLDivElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);
  const successHeadingRef = useRef<HTMLHeadingElement>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  const [showSticky, setShowSticky] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Honeypot — silent bot trap; hidden from assistive tech and keyboard users.
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [packageChoice, setPackageChoice] = useState(CUSTOM_QUOTE);
  // Name shown on the thank-you screen; the choice itself resets on success.
  const [submittedPackageName, setSubmittedPackageName] = useState<string | null>(null);
  // Older cached menus and test fixtures have no `packages` key.
  const packages = menu?.packages ?? NO_PACKAGES;
  // A pre-selected package that a refetch dropped reads as a custom quote.
  const chosenPackage = packages.find((p) => p.id === packageChoice);

  const seoDescription = useMemo(() => plainText(menu?.description), [menu?.description]);
  const seoImage = useMemo(() => ogImageUrl(menu?.og_image_url ?? menu?.cover_image_url), [menu?.og_image_url, menu?.cover_image_url]);
  const seoJsonLd = useMemo(() => {
    if (!menu) return undefined;
    return menuJsonLd({
      name: menu.title,
      description: menu.description,
      cover_image_url: menu.cover_image_url,
      og_image_url: menu.og_image_url,
      sections: ((menu.sections || []) as MenuSection[]).map((s) => ({
        title: s.title,
        description: s.description,
        items: (s.items || []).map((item) => ({
          menu_item: item.menu_item ? { name: item.menu_item.name, description: item.menu_item.description } : null,
        })),
      })),
    });
  }, [menu]);

  useDocumentMeta({
    title: menu ? `${menu.title} — Tristano's Catering` : undefined,
    description: seoDescription || undefined,
    canonical: slug ? publicMenuUrl(slug) : undefined,
    ogImage: seoImage,
    ogType: "website",
    twitterCard: "summary_large_image",
    jsonLd: seoJsonLd,
  });

  useEffect(() => {
    const handleScroll = () => setShowSticky(window.scrollY > 300);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Move focus to the success heading so SR users hear the confirmation and keyboard users land there.
  useEffect(() => {
    if (submitted) {
      successHeadingRef.current?.focus();
    }
  }, [submitted]);

  const scrollToForm = useCallback(() => {
    formRef.current?.scrollIntoView({ behavior: "smooth" });
    window.setTimeout(() => firstFieldRef.current?.focus(), 400);
  }, []);

  // "Get a quote for this package": pre-select it and go to the one form.
  // Leaving the thank-you screen means a second package inquiry needs no extra click.
  const quoteForPackage = useCallback((packageId: string) => {
    setPackageChoice(packageId);
    setSubmitted(false);
    scrollToForm();
  }, [scrollToForm]);

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
    setErrors({});
    setHoneypot("");
    setPackageChoice(CUSTOM_QUOTE);
  }, []);

  const handleFormKeyDown = useCallback((e: React.KeyboardEvent<HTMLFormElement>) => {
    // Escape that closed the Package dropdown was already handled (#932).
    if (e.defaultPrevented) return;
    if (e.key === "Escape" && !submitInquiry.isPending) {
      e.preventDefault();
      resetForm();
    }
  }, [resetForm, submitInquiry.isPending]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Silently drop bot submissions that tripped the honeypot.
    if (honeypot.trim() !== "") {
      setSubmitted(true);
      setSubmittedPackageName(null);
      setForm(EMPTY_FORM);
      setHoneypot("");
      return;
    }
    setErrors({});
    const result = inquirySchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }
    if (!menu) return;
    submitInquiry.mutate(
      {
        menu_id: menu.id,
        package_id: chosenPackage?.id,
        name: result.data.name,
        email: result.data.email,
        phone: result.data.phone || undefined,
        event_date: result.data.event_date || undefined,
        guest_count: typeof result.data.guest_count === "number" ? result.data.guest_count : undefined,
        message: typeof result.data.message === "string" ? result.data.message : undefined,
        idempotency_key: idempotencyKeyRef.current,
        content_category: menu.slug,
      },
      {
        onSuccess: () => {
          setSubmitted(true);
          setSubmittedPackageName(chosenPackage?.name ?? null);
          setPackageChoice(CUSTOM_QUOTE);
          setForm(EMPTY_FORM);
          setHoneypot("");
          // Fresh key for "Submit Another Inquiry" flow
          idempotencyKeyRef.current = crypto.randomUUID();
        },
      }
    );
  };

  if (isLoading) {
    return (
      <PublicLayout className="min-h-screen flex items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading menu…</p>
      </PublicLayout>
    );
  }

  // The wizard already has their request, so a missing menu still gets a
  // thanks. An outage falls through to the retry below.
  if (fromPlan && !menu && (!error || error instanceof NotFoundError)) {
    return (
      <PublicLayout className="min-h-screen bg-background">
        <main id="main-content" tabIndex={-1} className="focus:outline-none">
          <h1 className="sr-only font-heading">Thanks for your request</h1>
          <PlanThanksBanner planRef={planRef} menuTitle={null} />
          <PlanCallLine />
        </main>
        <PublicFooter />
      </PublicLayout>
    );
  }

  // Distinguish a real 404 (bad slug) from a service outage — the latter is
  // recoverable with a retry while the former is not.
  if (error && !(error instanceof NotFoundError)) {
    return (
      <PublicLayout className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">
            We're having trouble loading this menu
          </h1>
          <p className="text-muted-foreground mb-6">
            {fromPlan ? "We got your request. Please try again in a moment to see the menu." : "Please try again in a moment."}
          </p>
          <Button onClick={() => refetch()} disabled={isFetching} className="min-h-[44px]">
            {isFetching ? "Retrying…" : "Try again"}
          </Button>
        </div>
      </PublicLayout>
    );
  }

  if (!menu) {
    return (
      <PublicLayout className="min-h-screen flex items-center justify-center bg-background px-4">
        <div className="text-center max-w-md">
          <h1 className="font-heading text-2xl font-bold text-foreground mb-2">Menu Not Available</h1>
          <p className="text-muted-foreground mb-6">This menu is no longer available.</p>
          <Button asChild className="min-h-[44px]">
            <Link to="/packages">Browse our packages</Link>
          </Button>
        </div>
      </PublicLayout>
    );
  }

  const getItemPrice = (item: MenuSectionItem): number | null => {
    if (item.price_override != null) return item.price_override;
    const prices = item.menu_item?.prices ?? [];
    if (prices.length > 0) return prices[0].price;
    return null;
  };

  const formatPrice = (p: number) => `$${Number(p).toFixed(2)}`;
  const isSubmitting = submitInquiry.isPending;

  return (
    <PublicLayout className="min-h-screen bg-background">
      {/* Sticky CTA */}
      {showSticky && !fromPlan && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-primary/95 backdrop-blur-sm shadow-md">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
            <span className="text-primary-foreground font-logo font-semibold text-sm truncate">{menu.title}</span>
            <Button
              size="sm"
              variant="secondary"
              onClick={scrollToForm}
              className="shrink-0"
            >
              {menu.cta_text}
            </Button>
          </div>
        </div>
      )}

      <main id="main-content" tabIndex={-1} className="focus:outline-none">
      {fromPlan && <PlanThanksBanner planRef={planRef} menuTitle={menu.title} />}
      {/* Hero */}
      <div
        className="relative w-full flex flex-col overflow-hidden"
        style={{
          // minHeight is a floor: short content centres within it, long content grows the hero.
          minHeight: menu.cover_image_url ? "340px" : "200px",
          background: menu.cover_image_url ? undefined : `hsl(var(--primary))`,
        }}
      >
        {/* Background image via <picture> for modern format + responsive srcSet */}
        {menu.cover_image_url && (
          <>
            <HeroImage
              src={menu.cover_image_url}
              alt={menu.title}
              isAboveFold
            />
            {/* Dark gradient overlay */}
            <div
              className="absolute inset-0"
              style={{
                background: "linear-gradient(to bottom, hsl(var(--foreground) / 0.5), hsl(var(--foreground) / 0.7))",
              }}
            />
          </>
        )}
        <div className="relative flex-1 flex flex-col items-center justify-center text-center px-4 py-6">
          <h1 className="text-3xl md:text-5xl font-heading font-bold text-white mb-3">{menu.title}</h1>
          {menu.description && (
            <p className="text-white/80 max-w-2xl text-base md:text-lg font-secondary whitespace-pre-line">{menu.description}</p>
          )}
          {!fromPlan && (
            <Button size="lg" variant="secondary" className="mt-6" onClick={scrollToForm}>
              {menu.cta_text}
            </Button>
          )}
        </div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 py-12 space-y-12">
        {((menu.sections || []) as MenuSection[]).map((section) => (
          <section key={section.id}>
            <div className="mb-6">
              <h2 className="text-2xl font-heading font-bold text-foreground">{section.title}</h2>
              {section.description && (
                <p className="text-muted-foreground mt-1 font-secondary whitespace-pre-line">{section.description}</p>
              )}
            </div>
            <div className="divide-y divide-border">
              {(section.items || []).map((item) => {
                const mi = item.menu_item;
                if (!mi) return null;
                const desc = item.description_override || mi.description;
                const price = getItemPrice(item);
                // A flat section price_override carries no pricing_type → no unit.
                const priceRow = item.price_override == null ? mi.prices?.[0] ?? null : null;
                const priceUnit = menuPriceUnit(pricingTypes, priceRow);
                return (
                  <div key={item.id} className="flex items-start justify-between py-4 gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-heading font-semibold text-foreground">{mi.name}</h3>
                        {(mi.dietary_tags || []).map((tag: string) => (
                          <Badge key={tag} variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                            {getDietaryTagLabel(dietaryTags, tag)}
                          </Badge>
                        ))}
                      </div>
                      {desc && <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{desc}</p>}
                    </div>
                    {menu.show_prices && price != null && (
                      <span className="text-right text-sm font-semibold text-foreground whitespace-nowrap">
                        {formatPrice(price)}
                        {priceUnit && (
                          <span className="block text-xs font-normal text-muted-foreground">/ {priceUnit}</span>
                        )}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
        {packages.length > 0 && (
          <MenuPackagesSection
            packages={packages}
            showPrices={menu.show_prices}
            pricingTypes={pricingTypes}
            onQuote={fromPlan ? undefined : quoteForPackage}
          />
        )}
      </div>

      {/* Lead Capture Form */}
      <div
        ref={formRef}
        className="bg-muted py-16"
        style={{
          // Prevent the sticky CTA from covering the form heading after scrollIntoView.
          scrollMarginTop: "64px",
        }}
      >
        <div className="max-w-xl mx-auto px-4">
          {fromPlan ? (
            <PlanCallLine />
          ) : submitted ? (
            <div role="alert" aria-live="polite" className="text-center py-12">
              <h2
                ref={successHeadingRef}
                tabIndex={-1}
                className="text-2xl font-heading font-bold text-foreground mb-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-sm"
              >
                Thank You!
              </h2>
              <p className="text-muted-foreground">
                {submittedPackageName
                  ? `We'll reach out within 24 hours to plan your ${packageDisplayName(submittedPackageName)} menu.`
                  : "We've received your inquiry and will be in touch shortly."}
              </p>
              <Button className="mt-6" onClick={() => setSubmitted(false)}>
                Submit Another Inquiry
              </Button>
            </div>
          ) : (
            <>
              <div className="text-center mb-8">
                <h2 className="text-2xl font-heading font-bold text-foreground">{menu.cta_text}</h2>
                <p className="text-muted-foreground mt-1 font-secondary">
                  Tell us about your event and we'll get back to you with a custom quote.
                </p>
              </div>
              <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown} className="space-y-4" noValidate>
                {/* Honeypot — naive bots fill every input; real users can't see/tab this. */}
                <div aria-hidden="true" className="sr-only">
                  <Label htmlFor="menu-website">Website</Label>
                  <input
                    id="menu-website"
                    name="website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={honeypot}
                    onChange={(e) => setHoneypot(e.target.value)}
                  />
                </div>
                <fieldset disabled={isSubmitting} className="space-y-4 disabled:opacity-70">
                  {packages.length > 0 && (
                    <div>
                      <Label htmlFor="menu-package">Package</Label>
                      <Select value={chosenPackage?.id ?? CUSTOM_QUOTE} onValueChange={setPackageChoice}>
                        <SelectTrigger id="menu-package" className="mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={CUSTOM_QUOTE}>No package, I'd like a custom quote</SelectItem>
                          {packages.map((p) => (
                            <SelectItem key={p.id} value={p.id}>
                              {packageOptionLabel(p, menu.show_prices, pricingTypes)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="menu-name">Name *</Label>
                      <Input
                        id="menu-name"
                        ref={firstFieldRef}
                        autoComplete="name"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                      />
                      {errors.name && <p className="text-xs text-destructive mt-1">{errors.name}</p>}
                    </div>
                    <div>
                      <Label htmlFor="menu-email">Email *</Label>
                      <Input
                        id="menu-email"
                        type="email"
                        autoComplete="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                      />
                      {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <Label htmlFor="menu-phone">Phone</Label>
                      <Input
                        id="menu-phone"
                        type="tel"
                        autoComplete="tel"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="menu-event-date">Event Date</Label>
                      <Input
                        id="menu-event-date"
                        type="date"
                        value={form.event_date}
                        onChange={(e) => setForm({ ...form, event_date: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="menu-guest-count">Guest Count</Label>
                      <Input
                        id="menu-guest-count"
                        type="number"
                        min={1}
                        value={form.guest_count}
                        onChange={(e) => setForm({ ...form, guest_count: e.target.value })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="menu-message">Message</Label>
                    <Textarea
                      id="menu-message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      rows={4}
                      placeholder="Tell us about your event..."
                    />
                  </div>
                  <Button type="submit" className="w-full" size="lg" disabled={isSubmitting}>
                    {isSubmitting ? "Submitting..." : menu.cta_text}
                  </Button>
                </fieldset>
              </form>
            </>
          )}
        </div>
      </div>
      </main>

      <PublicFooter />
    </PublicLayout>
  );
}
